"use strict";
/**
 * Copyright © 2019 Johnson & Johnson
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var __createBinding = this && this.__createBinding || (Object.create ? function (o, m, k, k2) {
  if (k2 === undefined) k2 = k;
  Object.defineProperty(o, k2, {
    enumerable: true,
    get: function get() {
      return m[k];
    }
  });
} : function (o, m, k, k2) {
  if (k2 === undefined) k2 = k;
  o[k2] = m[k];
});

var __setModuleDefault = this && this.__setModuleDefault || (Object.create ? function (o, v) {
  Object.defineProperty(o, "default", {
    enumerable: true,
    value: v
  });
} : function (o, v) {
  o["default"] = v;
});

var __importStar = this && this.__importStar || function (mod) {
  if (mod && mod.__esModule) return mod;
  var result = {};
  if (mod != null) for (var k in mod) {
    if (k !== "default" && Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
  }

  __setModuleDefault(result, mod);

  return result;
};

var __rest = this && this.__rest || function (s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};

var __spreadArrays = this && this.__spreadArrays || function () {
  for (var s = 0, i = 0, il = arguments.length; i < il; i++) {
    s += arguments[i].length;
  }

  for (var r = Array(s), k = 0, i = 0; i < il; i++) {
    for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++) {
      r[k] = a[j];
    }
  }

  return r;
};

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.FALLBACK_SNAP_CLASSNAME = void 0;

var react_1 = __importStar(require("react"));

var throttle_1 = __importDefault(require("lodash/throttle"));

var mobx_react_lite_1 = require("mobx-react-lite");

var SlateSortableResizable_1 = __importStar(require("../SlateSortableResizable"));

var appendTailwindWidthClass_1 = require("./utils/appendTailwindWidthClass");

var RESIZE_THROTTLE_INTERVAL = 100;
exports.FALLBACK_SNAP_CLASSNAME = 'w-full';
/**
 * This is the component which wraps all items in the flow container. You probably
 * only need to use it directly if you are customizing the Admin UI.
 */

var SortableChild = mobx_react_lite_1.observer(function (props) {
  var _a;

  var flowContainerItem = props.flowContainerItem,
      _b = props.onResizeStop,
      onResizeStopProp = _b === void 0 ? function () {
    return undefined;
  } : _b,
      _c = props.snapData,
      snapData = _c === void 0 ? appendTailwindWidthClass_1.defaultSnapData : _c,
      _d = props.getDefaultWidth,
      getDefaultWidth = _d === void 0 ? function () {
    return exports.FALLBACK_SNAP_CLASSNAME;
  } : _d,
      _e = props.className,
      classNameProp = _e === void 0 ? '' : _e,
      ui = props.ui,
      children = props.children,
      restProps = __rest(props, ["flowContainerItem", "onResizeStop", "snapData", "getDefaultWidth", "className", "ui", "children"]); // These are the classes which control width when not resizing. The come from
  // the flow container item, or from the default width callback.


  var widthClasses = ((_a = flowContainerItem.wrapperProps) === null || _a === void 0 ? void 0 : _a.className) || getDefaultWidth(snapData); // We need the starting width so we can set defaultSize on the re-resizeable.
  // This ensures that we get back % during resize (otherwise we get pixels).

  var startingWidth = snapData({
    className: widthClasses
  }).width; // Track the snap width during resize so we can display it.

  var _f = react_1.useState(undefined),
      snapWidth = _f[0],
      setSnapWidth = _f[1];

  var onResize = react_1.useCallback(function (e, dir, ref) {
    var width = snapData({
      className: widthClasses,
      width: ref.style.width ? parseInt(ref.style.width, 10) : startingWidth
    }).width;
    setSnapWidth(width);
  }, [widthClasses, startingWidth]); // When resizing is complete, clear the snap width and notify the flow container
  // of the selected classes.

  var onResizeStop = react_1.useCallback(function (e, dir, ref) {
    setSnapWidth(undefined);
    var className = snapData({
      className: widthClasses,
      width: ref.style.width ? parseInt(ref.style.width, 10) : startingWidth
    }).className;
    if (onResizeStopProp) onResizeStopProp({
      className: className
    });
  }, [widthClasses, startingWidth, onResizeStopProp]); // we have to remove the width style when we are done resizing so that our responsive
  // classes will control width. Otherwise the width will be fixed at all breakpoints.
  // We need to do this onEffect bc we have to find the actual dom element.

  var resizing = snapWidth !== undefined;
  react_1.useEffect(function () {
    if (resizing) return;
    var elm = document.querySelector("[uuid='" + flowContainerItem.uuid + "']");

    if (elm) {
      elm.style.height = '';
      elm.style.width = '';
    }
  }, [resizing]);
  var minWidth = snapData({
    width: 0,
    className: ''
  }).width;

  var className = __spreadArrays(widthClasses.split(' '), classNameProp.split(' ')).join(' ');

  var SnapIndicator = SlateSortableResizable_1.getUI(ui).SnapIndicator;
  return react_1.default.createElement(SlateSortableResizable_1.default, __assign({
    uuid: flowContainerItem.uuid,
    onResize: onResize,
    onResizeStop: throttle_1.default(onResizeStop, RESIZE_THROTTLE_INTERVAL),
    // Set min width a bit smaller to ensure user can drag there, and provide snap back effect.
    minWidth: minWidth * 0.9 + "%",
    // Set default size to starting width so we get sizes in %.
    defaultSize: {
      width: startingWidth + "%",
      height: 'auto'
    },
    className: className,
    ui: ui
  }, restProps), resizing && // Show a label indicating where we'll snap to.
  react_1.default.createElement(SnapIndicator, null, Math.round(snapWidth) + "%"), children);
});
exports.default = SortableChild;